import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../lib/axios.js";

/**
 * Hook to fetch player data by wyId
 * @param {string|number} wyId - Player wyId identifier
 * @returns {Object} Query object with player data, loading and error states
 */
export const useGetPlayerByWyId = (wyId, leagueId = null) => {
  return useQuery({
    queryKey: ["player", wyId, leagueId],
    queryFn: async () => {
      const params = {};
      if (leagueId) params.leagueId = leagueId;
      const response = await axiosInstance.get(`/players/id/${wyId}`, { params });
      return response.data;
    },
    enabled: !!wyId, // Only run the query if wyId is provided
    retry: 1, // Retry failed query once
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
};

/**
 * Hook to fetch player attributes
 * @param {string} playerId - Player ID (MongoDB ID or wyId)
 * @returns {Object} Query object with player attributes, loading and error states
 */
export const useGetPlayerAttributes = (playerId) => {
  return useQuery({
    queryKey: ["player-attributes", playerId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/players/${playerId}/attributes`);
      return response.data;
    },
    enabled: !!playerId, // Only run the query if playerId is provided
    retry: 1, // Retry failed query once
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
};

/**
 * Hook to fetch players for a specific team
 * @param {string} teamId - Team ID (MongoDB ID or wyTeamId)
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether to enable the query
 * @returns {Object} Query object with team players, loading and error states
 */
export const useGetTeamPlayers = (teamId, options = {}) => {
  const {
    leagueId, // Optional – when supplied, backend will enrich with season stats
    season,   // Optional – numeric season; if omitted backend will use League.season
    enabled = true,
  } = options;

  return useQuery({
    queryKey: ["team-players", teamId, leagueId, season],
    queryFn: async () => {
      const params = {};
      if (leagueId) params.leagueId = leagueId;
      if (season !== undefined) params.season = season;

      const response = await axiosInstance.get(`/players/team/${teamId}`, { params });
      return response.data;
    },
    enabled: !!teamId && enabled, // Only run if teamId is provided and explicitly enabled
    retry: 1, // Retry failed query once
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
};

/**
 * Hook to fetch player market data including transfer history
 * @param {number} playerId - Player wyId
 * @returns {Object} Query object with full market data (includes transferHistory), loading and error states
 */
export const useGetPlayerSeasonStats = (wyId, leagueId, season, options = {}) => {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ['player-season-stats', wyId, leagueId, season],
    queryFn: async () => {
      const response = await axiosInstance.get(`/players/${wyId}/season-stats`, {
        params: { leagueId, season },
        // Treat 404 (no stats available) as a resolved response rather than an error.
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });

      // If the API responds with 404 it simply means stats for this player/season are not yet available.
      if (response.status === 404) {
        return {}; // Let the UI handle the empty stats scenario gracefully
      }

      // Otherwise return the actual data payload.
      return response.data;
    },
    enabled: !!wyId && !!leagueId && season != null && enabled,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPlayerTransferHistory = (playerId, leagueId) => {
  return useQuery({
    queryKey: ["player-market-data", playerId, leagueId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/transfers/market/player/${playerId}`, { params: { leagueId } });
      return response.data; // Return full market data object instead of just transferHistory
    },
    enabled: !!playerId && !!leagueId, // Require leagueId to ensure accurate ownership context
    retry: 1, // Retry failed query once
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
};

/**
 * Utility function to handle API errors
 * @param {Error} error - Error object from API call
 * @returns {Object} Formatted error object with message and status
 */
export const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      message: error.response.data.error || 'An error occurred',
      status: error.response.status
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      message: 'No response from server. Please check your connection.',
      status: 0
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      message: error.message || 'An unexpected error occurred',
      status: 0
    };
  }
};
